
// This file contains utilities for gestation data

type BabyDevelopmentInfo = {
  title: string;
  description: string;
  size?: string;
  weight?: string;
  milestones?: string[];
};

// Data for baby development by week
const babyDevelopmentByWeek: Record<number, BabyDevelopmentInfo> = {
  1: {
    title: "Conception",
    description: "La fécondation vient d'avoir lieu",
  },
  2: {
    title: "Début de l'embryon",
    description: "L'embryon s'implante dans l'utérus",
  },
  3: {
    title: "Formation des organes",
    description: "Les organes vitaux commencent à se former",
  },
  4: {
    title: "Développement cardiaque",
    description: "Le cœur commence à battre",
    size: "5 mm",
  },
  5: {
    title: "Formation des membres",
    description: "Les bras et les jambes commencent à se former",
    size: "1 cm",
  },
  6: {
    title: "Développement facial",
    description: "Les traits du visage commencent à se former",
    size: "1.3 cm",
  },
  7: {
    title: "Mouvements précoces",
    description: "Premiers mouvements, non perceptibles",
    size: "2 cm",
  },
  8: {
    title: "Tous les organes sont formés",
    description: "Les organes continuent à se développer",
    size: "2.5 cm",
    weight: "5 g",
  },
  9: {
    title: "Formation des muscles",
    description: "Les muscles se développent et se renforcent",
    size: "3 cm",
    weight: "10 g",
  },
  10: {
    title: "Développement du cerveau",
    description: "Croissance rapide du cerveau",
    size: "4 cm",
    weight: "15 g",
  },
  11: {
    title: "Bébé peut sucer son pouce",
    description: "Développement des réflexes",
    size: "5 cm",
    weight: "20 g",
  },
  12: {
    title: "Organes génitaux formés",
    description: "Le sexe devient identifiable",
    size: "6 cm",
    weight: "25 g",
  },
  13: {
    title: "Fin du premier trimestre",
    description: "Tous les organes sont formés et fonctionnels",
    size: "7 cm",
    weight: "30 g",
  },
  14: {
    title: "Début des cheveux",
    description: "Premiers cheveux et sourcils",
    size: "9 cm",
    weight: "45 g",
  },
  15: {
    title: "Formation des ongles",
    description: "Les ongles se développent",
    size: "10 cm",
    weight: "70 g",
  },
  16: {
    title: "Audition développée",
    description: "Bébé commence à entendre",
    size: "12 cm",
    weight: "100 g",
  },
  17: {
    title: "Mouvements plus forts",
    description: "Coups perceptibles par la mère",
    size: "13 cm",
    weight: "150 g",
  },
  18: {
    title: "Perception de la lumière",
    description: "Les yeux réagissent à la lumière",
    size: "15 cm",
    weight: "200 g",
  },
  19: {
    title: "Graisse brune",
    description: "Formation de la graisse brune pour la chaleur",
    size: "16 cm",
    weight: "250 g",
  },
  20: {
    title: "Mi-grossesse",
    description: "Bébé fait sensation avec ses mouvements",
    size: "17 cm",
    weight: "300 g",
  },
  21: {
    title: "Développement de l'équilibre",
    description: "Oreille interne développée",
    size: "18 cm",
    weight: "360 g",
  },
  22: {
    title: "Changement de position",
    description: "Mouvements plus fréquents",
    size: "19 cm",
    weight: "430 g",
  },
  23: {
    title: "Développement des poumons",
    description: "Les poumons continuent leur maturation",
    size: "20 cm",
    weight: "500 g",
  },
  24: {
    title: "Peau plus épaisse",
    description: "La peau devient moins transparente",
    size: "21 cm",
    weight: "600 g",
  },
  25: {
    title: "Tous les sens développés",
    description: "Goût, odorat, toucher, ouïe, vue",
    size: "22 cm",
    weight: "700 g",
  },
  26: {
    title: "Fin du deuxième trimestre",
    description: "Les chances de survie augmentent",
    size: "23 cm",
    weight: "820 g",
  },
  27: {
    title: "Ouverture des yeux",
    description: "Bébé peut ouvrir et fermer les yeux",
    size: "24 cm",
    weight: "950 g",
  },
  28: {
    title: "Cerveau plus complexe",
    description: "Développement des fonctions cérébrales",
    size: "25 cm",
    weight: "1.1 kg",
  },
  29: {
    title: "Production de surfactant",
    description: "Préparation des poumons à la respiration",
    size: "26 cm",
    weight: "1.25 kg",
  },
  30: {
    title: "Croissance rapide",
    description: "Prise de poids importante",
    size: "27 cm",
    weight: "1.4 kg",
  },
  31: {
    title: "Rythme veille/sommeil",
    description: "Établissement d'un cycle",
    size: "28 cm",
    weight: "1.6 kg",
  },
  32: {
    title: "Développement des ongles",
    description: "Les ongles atteignent le bout des doigts",
    size: "29 cm",
    weight: "1.8 kg",
  },
  33: {
    title: "Système immunitaire",
    description: "Anticorps transmis par la mère",
    size: "30 cm",
    weight: "2 kg",
  },
  34: {
    title: "Développement des reins",
    description: "Les reins sont pleinement fonctionnels",
    size: "32 cm",
    weight: "2.3 kg",
  },
  35: {
    title: "Réflexes développés",
    description: "Préparation à la naissance",
    size: "33 cm",
    weight: "2.5 kg",
  },
  36: {
    title: "Descente dans le bassin",
    description: "Bébé se positionne pour la naissance",
    size: "34 cm",
    weight: "2.7 kg",
  },
  37: {
    title: "Terme précoce",
    description: "Bébé est considéré à terme",
    size: "35 cm",
    weight: "3 kg",
  },
  38: {
    title: "Prêt pour le monde",
    description: "Poumons matures",
    size: "36 cm",
    weight: "3.2 kg",
  },
  39: {
    title: "À terme",
    description: "Bébé est complètement développé",
    size: "37 cm",
    weight: "3.3 kg",
  },
  40: {
    title: "Prêt pour la naissance",
    description: "L'arrivée est imminente",
    size: "38 cm",
    weight: "3.4 kg",
  },
};

/**
 * Get information about baby development for a specific week
 * @param week Current gestation week
 * @returns Development information for the week
 */
export const getBabyDevelopmentInfo = (week: number): BabyDevelopmentInfo => {
  // Ensure week is within valid range (1-40)
  const validWeek = Math.max(1, Math.min(40, week));
  return babyDevelopmentByWeek[validWeek] || {
    title: "Information non disponible",
    description: "Les données pour cette semaine ne sont pas disponibles",
  };
};

/**
 * Calculate remaining days in current week
 * @param day Current day of the week (0-6)
 * @returns Remaining days in current week
 */
export const getRemainingDaysInWeek = (day: number): number => {
  return 7 - day;
};

/**
 * Get trimester from current week
 * @param week Current gestation week
 * @returns Current trimester (1, 2, or 3)
 */
export const getCurrentTrimester = (week: number): number => {
  if (week <= 13) return 1;
  if (week <= 26) return 2;
  return 3;
};

/**
 * Get recommended tests for current week
 * @param week Current gestation week
 * @returns Array of recommended tests
 */
export const getRecommendedTests = (week: number): string[] => {
  // Example implementation with common tests by trimester
  if (week <= 13) {
    return [
      "Échographie du premier trimestre",
      "Test sanguin (groupe sanguin, facteur rhésus)",
      "Dépistage de la trisomie 21",
    ];
  } else if (week <= 26) {
    return [
      "Échographie morphologique",
      "Test de dépistage du diabète gestationnel",
      "Test sanguin pour l'anémie",
    ];
  } else {
    return [
      "Échographie du troisième trimestre",
      "Streptocoque B",
      "Surveillance de la tension artérielle",
    ];
  }
};
